import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import Select from "react-select";
import MCQViewer from "../../common/MCQViewer";
import TrueFalseViewer from "../../common/TrueFalseViewer";
import MatchViewer from "../../common/MatchViewer";
import FillUpsViewer from "../../common/FillUpsViewer";
import AnalysisComponent from "../../common/AnalysisComponent";
import { GENERATE_CONTENT_URL } from "../../../helper/apiurls/apiurls";
import { getCourses } from "../../../helper/apicalls/course";
import { createInitialTest } from "../../../helper/apicalls/initialTest";
import GlobalLoader from "../../common/GlobalLoader";
import "../../../styles/admin/initialTest.scss";
import Cookies from "js-cookie";

const CONTENT_TYPE_OPTIONS = [
  { value: "mcq", label: "MCQ" },
  { value: "match", label: "Match" },
  { value: "fillups", label: "Fill Ups" },
  { value: "trueorfalse", label: "True or False" },
  // { value: "analysis", label: "Analysis" },
];

const CreateInitialTestPage = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const { setHeaderName } = useOutletContext();
  const token = Cookies.get("admin_token");
  const [courseName, setCourseName] = useState("");
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [contentType, setContentType] = useState("mcq");
  const [generatedContent, setGeneratedContent] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState("");

  const websocketRef = useRef(null);

  const fetchCourseName = async () => {
    try {
      const courses = await getCourses();
      const course = Array.isArray(courses)
        ? courses.find((c) => c.key === key)
        : null;
      if (course) {
        setCourseName(course.name);
        setHeaderName(`Initial Test - ${course.name}`);
      } else {
        setHeaderName("Initial Test");
      }
    } catch (err) {
      console.error("Error fetching course name:", err);
      setHeaderName("Initial Test");
    }
  };

  const handleGenerate = () => {
    if (!prompt.trim()) {
      setError("Please enter a prompt");
      return;
    }

    setIsGenerating(true);
    setError("");

    try {
      if (websocketRef.current) {
        websocketRef.current.close();
      }

      websocketRef.current = new WebSocket(GENERATE_CONTENT_URL);

      websocketRef.current.onopen = () => {
        let message = {
          prompt,
          type: contentType,
          field: "course",
          key: key || "",
          token: token,
        };
        websocketRef.current.send(JSON.stringify(message));
      };

      websocketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.status === "completed" && data.data && data.data.content) {
            setGeneratedContent(data.data.content);
          } else if (data.type === "content" && data.content) {
            setGeneratedContent(data.content);
          } else if (data.type === "error") {
            setError(data.message || "Error generating content");
          } else {
            if (typeof event.data === "string") {
              setGeneratedContent(event.data);
            } else {
              setGeneratedContent(JSON.stringify(event.data, null, 2));
            }
          }
        } catch (err) {
          if (typeof event.data === "string") {
            setGeneratedContent(event.data);
          } else {
            setGeneratedContent(JSON.stringify(event.data, null, 2));
          }
        } finally {
          setIsGenerating(false);
        }
      };

      websocketRef.current.onerror = (err) => {
        console.error("WebSocket error:", err);
        setError("Failed to connect to content generation service");
        setIsGenerating(false);
      };

      websocketRef.current.onclose = () => {};
    } catch (err) {
      console.error("Error generating content:", err);
      setError("Failed to generate content. Please try again.");
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Please enter a name for the test");
      return;
    }
    if (!generatedContent) {
      setError("Please generate content before saving");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      const sequence = 1;

      const contentItem = {
        type: contentType,
        sequence,
        ...(categories && { categories }),
        content:
          typeof generatedContent === "string"
            ? generatedContent
            : JSON.stringify(generatedContent),
      };

      const payload = {
        name: name.trim(),
        course_key: key,
        content: [contentItem],
        pass_percentage: 60,
      };

      await createInitialTest(payload);
      navigate("/admin/initial-test");
    } catch (err) {
      console.error("Error saving initial test:", err);
      setError("Failed to save initial test. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderPreview = () => {
    if (!generatedContent) {
      return <div className="content-preview">No content generated yet.</div>;
    }

    switch (contentType) {
      case "mcq":
        return (
          <MCQViewer
            questionData={generatedContent}
            title="Initial Test - MCQ"
          />
        );
      case "trueorfalse":
        return (
          <TrueFalseViewer
            questionData={generatedContent}
            title="Initial Test - True / False"
          />
        );
      case "match":
        return (
          <MatchViewer
            questionData={generatedContent}
            title="Initial Test - Match"
          />
        );
      case "fillups":
        return (
          <FillUpsViewer
            questionData={generatedContent}
            title="Initial Test - Fill Ups"
          />
        );
      case "analysis":
        return (
          <AnalysisComponent
            data={generatedContent}
            title="Initial Test - Analysis"
          />
        );
      default:
        return (
          <div className="content-preview">
            <pre>
              {typeof generatedContent === "string"
                ? generatedContent
                : JSON.stringify(generatedContent, null, 2)}
            </pre>
          </div>
        );
    }
  };

  useEffect(() => {
    fetchCourseName();

    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
      }
    };
  }, [key, setHeaderName]);
  return (
    <div className="initial-test-page">
      <GlobalLoader visible={isSaving} text="Saving initial test..." />
      <div className="initial-test-page__card">
        <div className="initial-test-page__header">
          <h2 className="initial-test-page__title">
            Create Initial Test {courseName ? `for ${courseName}` : ""}
          </h2>
        </div>

        <div className="initial-test-page__body">
          <div className="initial-test-page__input-section">
            <div className="initial-test-page__field">
              <label className="initial-test-page__label">Test Name</label>
              <input
                type="text"
                className="initial-test-page__input"
                placeholder="Enter a name for this initial test"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="initial-test-page__field">
              <label className="initial-test-page__label">Content Type</label>
              <Select
                classNamePrefix="content-generator-select"
                value={CONTENT_TYPE_OPTIONS.find(
                  (o) => o.value === contentType
                )}
                onChange={(opt) => setContentType(opt?.value || "mcq")}
                options={CONTENT_TYPE_OPTIONS}
              />
            </div>

            <div className="initial-test-page__field">
              <label className="initial-test-page__label">Categories</label>
              <Select
                classNamePrefix="content-generator-select"
                value={
                  categories
                    ? {
                        value: categories,
                        label: categories
                          .replace("_", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase()),
                      }
                    : null
                }
                onChange={(selectedOption) =>
                  setCategories(selectedOption ? selectedOption.value : "")
                }
                options={[
                  { value: "direct", label: "Direct" },
                  {
                    value: "critical_thinking",
                    label: "Critical Thinking",
                  },
                  { value: "reasoning", label: "Reasoning" },
                  { value: "application", label: "Application" },
                ]}
                placeholder="Select a category"
              />
            </div>

            <div className="initial-test-page__field">
              <label className="initial-test-page__label">Prompt</label>
              <textarea
                className="initial-test-page__textarea"
                placeholder="Enter a prompt to generate questions for this course"
                value={prompt}
                onChange={(e) => {
                  setPrompt(e.target.value);
                  setError("");
                }}
              />
              {error && <div className="initial-test-page__error">{error}</div>}

              <div className="initial-test-page__generate-row">
                <button
                  className="initial-test-page__generate-btn"
                  onClick={handleGenerate}
                  disabled={isGenerating}
                >
                  {isGenerating ? "Generating..." : "Generate"}
                </button>
              </div>
            </div>
          </div>

          <div className="initial-test-page__preview-section">
            <h3 className="initial-test-page__preview-title">Preview</h3>
            {renderPreview()}
          </div>
        </div>

        <div className="initial-test-page__actions">
          <button
            className="initial-test-page__btn primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            Save Initial Test
          </button>
          <button
            className="initial-test-page__btn secondary"
            onClick={() => navigate("/admin/initial-test")}
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateInitialTestPage;
